import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  centsToDollars,
  type ReferralCodeRow,
  type ReferralPromotionRow,
  type ReferralRedemptionRow,
} from "@/lib/referral";

const humanizeStatus = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

/**
 * Bring-a-Friend management. Promotion settings live per class + canonical
 * session, so a September Basketball referral can never apply to October.
 * Turning a promotion off blocks new redemptions and preserves all history.
 */
export default function ReferralsPage() {
  const [promos, setPromos] = useState<ReferralPromotionRow[]>([]);
  const [codes, setCodes] = useState<ReferralCodeRow[]>([]);
  const [redemptions, setRedemptions] = useState<ReferralRedemptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [p, c, r] = await Promise.all([
      supabase.from("referral_promotions").select("*").order("class_id"),
      supabase.from("referral_codes").select("*").order("created_at", { ascending: false }),
      supabase.from("referral_redemptions").select("*").order("created_at", { ascending: false }),
    ]);
    setPromos((p.data as unknown as ReferralPromotionRow[]) ?? []);
    setCodes((c.data as unknown as ReferralCodeRow[]) ?? []);
    setRedemptions((r.data as unknown as ReferralRedemptionRow[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const savePromo = async (id: string, patch: Partial<ReferralPromotionRow>) => {
    setSaving(id);
    const { error } = await supabase
      .from("referral_promotions")
      .update(patch as never)
      .eq("id", id);
    setSaving(null);
    if (error) {
      toast.error("Couldn't save that change.");
      return;
    }
    setPromos((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    toast.success("Saved.");
  };

  // Only redeemed rows count as successful referrals — issued-but-unused codes
  // and released reservations never inflate the numbers.
  const summary = useMemo(() => {
    const redeemed = redemptions.filter((r) => r.status === "redeemed");
    const discounts = redeemed.reduce((n, r) => n + r.discount_cents, 0);
    const collected = redeemed.reduce((n, r) => n + r.final_amount_cents, 0);
    return {
      issued: codes.length,
      successful: redeemed.length,
      seats: redeemed.length,
      discounts,
      collected,
      average: redeemed.length ? Math.round(discounts / redeemed.length) : 0,
    };
  }, [codes, redemptions]);

  return (
    <AdminShell>
      <div className="space-y-6">
        <header>
          <h1 className="font-heading text-2xl font-bold text-foreground">Bring a Friend</h1>
          <p className="text-sm text-muted-foreground">
            Referral promotions, issued codes, and redemptions.
          </p>
        </header>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { label: "Codes issued", value: String(summary.issued), hint: "All time" },
            { label: "Successful referrals", value: String(summary.successful), hint: "Redeemed codes · all time" },
            { label: "Seats added by referrals", value: String(summary.seats), hint: "One seat per redemption" },
            { label: "Total referral discounts", value: centsToDollars(summary.discounts), hint: "All time" },
            { label: "Revenue after discounts", value: centsToDollars(summary.collected), hint: "Collected from referred families" },
            { label: "Average discount", value: centsToDollars(summary.average), hint: "Per redeemed code" },
          ].map((m) => (
            <Card key={m.label}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{m.label}</p>
                <p className="text-xl font-semibold text-foreground">{m.value}</p>
                 <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{m.hint}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Promotions by class &amp; session</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && promos.length === 0 && (
              <p className="text-sm text-muted-foreground">No referral promotions configured yet.</p>
            )}
            {promos.map((p) => (
              <div
                key={p.id}
                className="rounded-lg border border-border p-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-center"
              >
                <div>
                  <p className="font-medium text-foreground">
                    {p.class_name || p.class_id}{" "}
                    <span className="text-muted-foreground">— {p.canonical_session_label}</span>
                  </p>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <div className="w-28">
                      <Label className="text-xs">Discount %</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        defaultValue={Number(p.discount_percent)}
                        onBlur={(e) =>
                          savePromo(p.id, { discount_percent: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div className="w-32">
                      <Label className="text-xs">Max discount ($)</Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        defaultValue={(p.max_discount_cents / 100).toFixed(2)}
                        onBlur={(e) =>
                          savePromo(p.id, {
                            max_discount_cents: Math.round(Number(e.target.value) * 100),
                          })
                        }
                      />
                    </div>
                    <div className="w-36">
                      <Label className="text-xs">Uses per referrer</Label>
                      <Input
                        type="number"
                        min={1}
                        defaultValue={p.max_redemptions_per_code}
                        onBlur={(e) =>
                          savePromo(p.id, { max_redemptions_per_code: Number(e.target.value) })
                        }
                      />
                    </div>
                    <div className="w-44">
                      <Label className="text-xs">Expires (optional)</Label>
                      <Input
                        type="date"
                        defaultValue={p.expires_at ? p.expires_at.slice(0, 10) : ""}
                        onBlur={(e) =>
                          savePromo(p.id, {
                            expires_at: e.target.value
                              ? new Date(`${e.target.value}T23:59:59`).toISOString()
                              : null,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-self-start md:justify-self-end">
                  <Badge variant={p.active ? "default" : "secondary"}>
                    {p.active ? "Active" : "Off"}
                  </Badge>
                  <Switch
                    checked={p.active}
                    disabled={saving === p.id}
                    onCheckedChange={(v) => savePromo(p.id, { active: v })}
                    aria-label="Toggle promotion"
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Referrals</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {redemptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No referral codes have been used yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Session</th>
                    <th className="py-2 pr-3">Referred family</th>
                    <th className="py-2 pr-3 text-right">List</th>
                    <th className="py-2 pr-3 text-right">Discount</th>
                    <th className="py-2 pr-3 text-right">Collected</th>
                    <th className="py-2 pr-3">Redeemed</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="py-2 pr-3 font-mono">{r.code_used}</td>
                      <td className="py-2 pr-3">{humanizeStatus(r.status)}</td>
                      <td className="py-2 pr-3">{r.canonical_session_label}</td>
                      <td className="py-2 pr-3">
                        {r.referred_child_name || r.referred_email || "—"}
                      </td>
                      <td className="py-2 pr-3 text-right">{centsToDollars(r.list_price_cents)}</td>
                      <td className="py-2 pr-3 text-right">−{centsToDollars(r.discount_cents)}</td>
                      <td className="py-2 pr-3 text-right">
                        {centsToDollars(r.final_amount_cents)}
                      </td>
                      <td className="py-2 pr-3">
                        {r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Issued codes</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {codes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Codes are issued automatically when a family registers for a session with an active
                promotion.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3">Child</th>
                    <th className="py-2 pr-3">Session</th>
                    <th className="py-2 pr-3 text-right">Used</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => (
                    <tr key={c.id} className="border-t border-border">
                      <td className="py-2 pr-3 font-mono">{c.code}</td>
                      <td className="py-2 pr-3">{c.referrer_child_name || "—"}</td>
                      <td className="py-2 pr-3">{c.canonical_session_label}</td>
                      <td className="py-2 pr-3 text-right">{c.redemptions_used}</td>
                      <td className="py-2 pr-3">{c.active ? "Code active" : "Code disabled"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}
